import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { gql, useQuery } from '@apollo/client';
import { TrendingUp as TrendingUpIcon, ArrowRight as ArrowRightIcon, Calendar as CalendarIcon } from 'lucide-react';
import InfoTooltip from '../common/InfoTooltip';
import { currencySymbol } from '../../utils/formatters';

// Query valuation with the same approach as the main valuation page
// The backend uses the user's stored financial data (gross_expenses, revenue fields, etc.)
// to compute the valuation, so we just need to specify which business lines to include
const GET_MULTI_LINE_VALUATION_SNAPSHOT = gql`
  query GetMultiLineValuationSnapshot($input: MultiLineValuationInput!) {
    multiLineValuation(input: $input) {
      combined_summary
      combined_summary_reported
      combined_summary_adjusted
      implied_multiples
      adjustment_impact
      weights
      features
      lines {
        line
        methods
      }
    }
  }
`;

const GET_CURRENT_USER_VAL = gql`
  query GetCurrentUserValForMultiLineSnapshot {
    getCurrentUser {
      id
      valuation_last_accessed_date
      gross_production
      branch_net_payout
      fee_based_revenue
      trailer_revenue
      transactional_revenue
      direct_revenue
      year_1_gross
      year_2_gross
      year_3_gross
      year_4_gross
      year_5_gross
      use_revenue_growth_rate
      revenue_growth_rate
      use_benchmark_expense_percentage
      gross_expenses
      company {
        id
        features
        allow_user_to_specify_revenue_growth_rate
      }
    }
  }
`;

// Mirrors compactAvgGrowth in MultiLineValuationContainer.jsx deriveUserDefaults.
// Average of period-over-period growth rates across y1..y5, divided by the count
// of historical points (5) — matches the backend's RjGrossRevenueDetail behavior.
const compactAvgGrowth = (arr) => {
  const vals = (arr || []).map((v) => Number(v || 0)).filter((v) => Number.isFinite(v));
  const cleaned = vals.filter((v) => v);
  if (cleaned.length < 2) return undefined;
  const growths = [];
  for (let i = 0; i < cleaned.length - 1; i++) {
    const current = cleaned[i];
    const next = cleaned[i + 1];
    if (current && Number.isFinite(current) && Number.isFinite(next)) {
      growths.push((current - next) / current);
    }
  }
  if (!growths.length) return undefined;
  const avg = growths.reduce((s, v) => s + v, 0) / cleaned.length;
  return Number.isFinite(avg) ? avg : 0;
};

// Build the multiLineValuation input from the user's stored fields.
// Mirrors what MultiLineValuationContainer's deriveUserDefaults+auto-benchmark
// recalc sends. For the snapshot we only populate the wealth line — that is
// the only line RJ-style tenants use, and unpopulated lines fall back to
// the backend's default ($0) which matches the valuation page's behavior
// when those lines have no input data.
const buildSnapshotInput = (user) => {
  const fee = Number(user?.fee_based_revenue || 0);
  const trail = Number(user?.trailer_revenue || 0);
  const trans = Number(user?.transactional_revenue || 0);
  const direct = Number(user?.direct_revenue || 0);
  // Match the valuation page's expense logic (MultiLineValuation.jsx useBenchmark
  // default) and legacy RjEbocEstimate#overhead_expenses: use the revenue-based
  // benchmark OpEx only when the tenant feature config allows it AND the user's
  // use_benchmark_expense_percentage flag is set; otherwise fall back to
  // gross_expenses. Previously this always sent the benchmark OpEx, so the
  // LaunchPad DCF diverged from the valuation page for users with the flag off.
  //
  // In benchmark mode we send no figure at all — the snapshot makes a single request
  // and has no result to read the benchmark from. The backend fills it in, which also
  // keeps the RJAS override applied; the ladder that used to live here did not have one.
  const features = Array.isArray(user?.company?.features) ? user.company.features : [];
  const benchmarkToggleAvailable = !features.includes('employee_based_advisors') || features.includes('rjas_benchmark_expenses');
  const useBenchmark = benchmarkToggleAvailable && !!user?.use_benchmark_expense_percentage;
  const benchOpEx = useBenchmark ? null : Number(user?.gross_expenses || 0);
  const payout = (() => {
    const n = Number(user?.branch_net_payout);
    return Number.isFinite(n) ? (n > 1 ? n / 100 : n) : 0;
  })();
  const history = {
    y1: Number(user?.year_1_gross || 0) || undefined,
    y2: Number(user?.year_2_gross || 0) || undefined,
    y3: Number(user?.year_3_gross || 0) || undefined,
    y4: Number(user?.year_4_gross || 0) || undefined,
    y5: Number(user?.year_5_gross || 0) || undefined,
  };
  const allowManualGrowth = !!user?.company?.allow_user_to_specify_revenue_growth_rate;
  const growth = allowManualGrowth
    ? (Number.isFinite(Number(user?.revenue_growth_rate)) ? Number(user?.revenue_growth_rate) : undefined)
    : compactAvgGrowth([
      user?.year_1_gross, user?.year_2_gross, user?.year_3_gross, user?.year_4_gross, user?.year_5_gross,
    ]);

  const inputs = {
    feeBasedRevenue: fee,
    trailCommissions: trail,
    transactionalCommissions: trans,
    otherDirectRevenue: direct,
    faRetentionPercent: payout,
    history,
  };
  if (benchOpEx !== null) inputs.operatingExpenses = benchOpEx;
  if (growth !== undefined) inputs.growth = growth;

  return {
    lines: [
      { name: 'wealth', inputs },
      { name: 'life' },
      { name: 'pc' },
      { name: 'group' },
    ],
    expenses: { useBenchmark },
  };
};

const APPROACH_TOOLTIPS = {
  dcf: 'Discounted Cash Flow (DCF) estimates business value by projecting future cash flows and discounting them to present value.',
  mor: 'Multiple of Revenue (MOR) applies a market-derived multiple to normalized recurring revenue to estimate value.',
  ebitda: 'EBITDA valuation applies a market multiple to earnings before interest, taxes, depreciation, and amortization.',
  asset: 'Asset-based valuation estimates value based on net asset value after liabilities.',
};

// When the DCF tile shows a range, the low end is 1× the advisor's trailing
// 12-month gross production (a conservative floor) and the high end is the DCF
// valuation. Spelling this out keeps the two numbers from confusing advisors —
// the exact concern the RJA team raised when the legacy range was dropped.
const DCF_RANGE_TOOLTIP = `${APPROACH_TOOLTIPS.dcf} The low end reflects 1× your trailing 12-month gross production; the high end is the DCF valuation.`;

// Clarify (for feed-driven tenants like RJA/ICD) that T12 is a fiscal year-end
// snapshot, not a rolling month-end figure — appended to the DCF tooltip when the
// valuation_t12_year_end_note feature is enabled.
const T12_YEAR_END_NOTE = 'T12 (trailing 12 months) reflects fiscal year-end figures, not month-end.';

const formatCurrency = (value) => {
  const v = Number(value || 0);
  const sym = currencySymbol();
  if (v >= 1000000) return `${sym}${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${sym}${(v / 1000).toFixed(0)}K`;
  return `${sym}${v.toLocaleString()}`;
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return null;
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_) {
    return null;
  }
};

const getMethodEnabled = (features = {}, weights = {}) => {
  const fallback = {
    mor: Number(weights?.mor || 0) > 0,
    dcf: Number(weights?.dcf || 0) > 0,
    ebitda: Number(weights?.ebitda || 0) > 0,
    asset: Number(weights?.asset || 0) > 0,
  };
  return {
    mor: typeof features?.mor === 'boolean' ? features.mor : fallback.mor,
    dcf: typeof features?.dcf === 'boolean' ? features.dcf : fallback.dcf,
    ebitda: typeof features?.ebitda === 'boolean' ? features.ebitda : fallback.ebitda,
    asset: typeof features?.asset === 'boolean' ? features.asset : fallback.asset,
  };
};

// Empty State Component
const EmptyState = ({ canCreate = true, showHeaderIcon = true, intl }) => (
  <div className="space-y-4 h-full flex flex-col">
    <div className={showHeaderIcon ? "flex items-start gap-4" : ""}>
      {showHeaderIcon && (
        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-succession-success/10">
          <TrendingUpIcon className="w-6 h-6 text-succession-success" />
        </div>
      )}
      <div>
        <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>
          {intl.formatMessage({ id: 'succession.launchpad.valuation_snapshot_title' })}
        </h3>
        <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>
          {intl.formatMessage({ id: 'succession.launchpad.valuation_snapshot_subtitle_multi_empty' })}
        </p>
      </div>
    </div>

    <p className="text-sm text-succession-text-secondary leading-relaxed flex-1">
      {intl.formatMessage({ id: 'succession.launchpad.valuation_snapshot_description_multi' })}
    </p>

    {canCreate ? (
      <a
        href="/succession/valuation/multi"
        className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium rounded-md bg-succession-text-primary text-succession-bg-primary hover:opacity-90 transition-opacity group"
      >
        {intl.formatMessage({ id: 'succession.launchpad.view_multi_line_valuation' })}
        <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
      </a>
    ) : (
      <div className="text-center py-3 px-4 bg-succession-bg-secondary rounded-md">
        <p className="text-sm text-succession-text-secondary" style={{ margin: 0 }}>
          {intl.formatMessage({ id: 'succession.launchpad.contact_advisor_view_valuation' })}
        </p>
      </div>
    )}
  </div>
);

const METHOD_COLORS = {
  dcf: 'var(--succession-primary, #5489B6)',
  mor: 'var(--succession-secondary, #002949)',
  ebitda: 'var(--succession-accent, #5B5E5A)',
  asset: 'var(--succession-neutral, #666666)',
};

const METHOD_LABELS = {
  dcf: 'DCF',
  mor: 'MOR',
  ebitda: 'EBITDA',
  asset: 'Asset-Based',
};

// A line is "active" for tile purposes only if it has DCF projections AND
// at least one of revenue or DCF cash flow is non-zero. Matches the
// valuation page's DCF tab filter (MultiLineValuation.jsx:3306) plus a
// guard against backend-generated synthetic lines with zero cash flow.
const isLineActive = (line) => {
  const dcf = line?.methods?.dcf;
  if (!Array.isArray(dcf?.projections) || dcf.projections.length === 0) return false;
  const rev = Number(dcf.projections[0]?.revenue || 0);
  const pvSum = dcf.projections.reduce((s, p) => s + Number(p?.pvFcf || 0), 0);
  return rev > 0 || pvSum > 0;
};

// Compute each method's reported total across active lines.
// DCF mirrors the valuation page's DCF tab exactly: Σ (pvFcf + pvTerminal)
// per line. We deliberately do NOT use backend dcf.mid — for some tenant
// configurations the backend returns dcf.mid as a post-normalization
// (adjusted) value while the valuation page renders the reported (raw
// cash-flow) total by default. Anchoring to raw projections keeps the
// snapshot in sync with the methodology tab.
//
// Other methods (MOR/EBITDA/Asset) use backend mid since there's no
// equivalent raw-projection field exposed for them.
const computeMethodTotals = (data) => {
  const linesArr = (data?.lines || []).filter(isLineActive);
  const totals = { mor: 0, dcf: 0, ebitda: 0, asset: 0 };
  linesArr.forEach((l) => {
    const m = l?.methods || {};
    const dcf = m.dcf || {};
    const proj = Array.isArray(dcf.projections) ? dcf.projections : [];
    const pvSum = proj.reduce((s, p) => s + Number(p?.pvFcf || 0), 0);
    const pvTerminal = Number(dcf.pvTerminal || 0);
    totals.dcf += pvSum + pvTerminal;
    ['mor', 'ebitda', 'asset'].forEach((k) => {
      if (m[k] && typeof m[k].mid === 'number') totals[k] += m[k].mid;
    });
  });
  return totals;
};

// Tile-list state — shown when the multi_line_valuation_summary feature is OFF.
// Each enabled methodology gets its own tile with no blended/aggregate number.
const MethodologyTileList = ({ data, lastUpdated, showHeaderIcon = true, compact = false, grossProduction = 0, rangeEnabled = false, t12NoteEnabled = false, intl }) => {
  const weights = data?.weights || {};
  const features = data?.features || {};
  const methodEnabled = getMethodEnabled(features, weights);
  const methodTotals = computeMethodTotals(data);


  const tiles = ['dcf', 'mor', 'ebitda', 'asset']
    .filter((k) => methodEnabled[k] && Number(methodTotals[k] || 0) > 0)
    .map((k) => {
      const value = Number(methodTotals[k] || 0);
      const tile = {
        key: k,
        label: METHOD_LABELS[k],
        value,
        color: METHOD_COLORS[k],
      };
      // Reintroduce the legacy valuation range for the DCF approach (feature-gated):
      // floor at 1× the advisor's trailing 12-month gross production, ceiling at the
      // DCF valuation, always displayed low → high regardless of which is larger.
      if (k === 'dcf' && rangeEnabled) {
        const floor = Number(grossProduction || 0);
        if (floor > 0 && value > 0 && floor !== value) {
          tile.range = [Math.min(floor, value), Math.max(floor, value)];
        }
      }
      return tile;
    });

  const formattedLastUpdated = formatRelativeTime(lastUpdated);
  const isHero = tiles.length === 1;
  const valueSize = (() => {
    if (compact && isHero) return 'text-3xl';
    if (compact) return 'text-xl';
    if (isHero) return 'text-5xl';
    if (tiles.length === 2) return 'text-3xl';
    return 'text-2xl';
  })();
  // Ranges hold ~2× the characters of a single value, so step the type down a
  // notch to keep them on one line inside the (often half-width) widget card.
  const sizeForTile = (tile) => {
    if (!tile.range) return valueSize;
    if (compact && isHero) return 'text-xl';
    if (compact) return 'text-base';
    if (isHero) return 'text-3xl';
    if (tiles.length === 2) return 'text-2xl';
    return 'text-xl';
  };
  const renderTileValue = (tile) => (tile.range
    ? `${formatCurrency(tile.range[0])} – ${formatCurrency(tile.range[1])}`
    : formatCurrency(tile.value));
  const tooltipFor = (tile) => {
    const base = tile.range && tile.key === 'dcf' ? DCF_RANGE_TOOLTIP : APPROACH_TOOLTIPS[tile.key];
    if (!base) return base;
    // Append the T12 fiscal-year-end clarification to the DCF tooltip for
    // feed-driven tenants (RJA/ICD).
    return tile.key === 'dcf' && t12NoteEnabled ? `${base} ${T12_YEAR_END_NOTE}` : base;
  };
  const tilePaddingClass = (() => {
    if (isHero && compact) return 'p-5';
    if (isHero) return 'p-7';
    if (compact) return 'px-3 py-2.5';
    return 'px-4 py-3';
  })();

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`${compact ? 'mb-4' : 'mb-6'} ${showHeaderIcon ? 'flex items-start gap-4' : ''}`}>
        {showHeaderIcon && (
          <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-succession-success/10">
            <TrendingUpIcon className="w-6 h-6 text-succession-success" />
          </div>
        )}
        <div>
          <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>
            {intl.formatMessage({ id: 'succession.launchpad.valuation_snapshot_title' })}
          </h3>
          <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>
            {intl.formatMessage({ id: 'succession.launchpad.valuation_snapshot_subtitle_by_methodology' })}
          </p>
        </div>
      </div>

      {/* Tiles */}
      <div className={`flex-1 flex flex-col ${compact ? 'gap-2' : 'gap-3'} ${isHero ? 'justify-center' : ''}`}>
        {tiles.map((tile) => (
          <div
            key={tile.key}
            className={`relative ${tilePaddingClass} bg-succession-bg-secondary/40 border border-succession-border rounded-lg overflow-hidden`}
          >
            <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: tile.color }} aria-hidden="true" />
            {isHero ? (
              <div className="pl-3 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-2">
                  <span className="text-sm font-medium text-succession-text-secondary">{tile.label}</span>
                  {tooltipFor(tile) && <InfoTooltip content={tooltipFor(tile)} />}
                </div>
                <div className={`font-bold text-succession-text-primary ${sizeForTile(tile)}`}>
                  {renderTileValue(tile)}
                </div>
              </div>
            ) : (
              <div className="pl-3 flex items-baseline justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-succession-text-secondary">{tile.label}</span>
                  {tooltipFor(tile) && (
                    <InfoTooltip content={tooltipFor(tile)} className={compact ? 'scale-90' : ''} />
                  )}
                </div>
                <span className={`font-semibold text-succession-text-primary whitespace-nowrap ${sizeForTile(tile)}`}>
                  {renderTileValue(tile)}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Last Updated */}
      {formattedLastUpdated && (
        <div className={`${compact ? 'mt-3' : 'mt-4'} flex items-center gap-2 text-sm text-succession-text-secondary`}>
          <CalendarIcon className="w-4 h-4" />
          <span>{intl.formatMessage({ id: 'succession.launchpad.last_updated' }, { time: formattedLastUpdated })}</span>
        </div>
      )}

      {/* Footer */}
      <div className={`${compact ? 'mt-3' : 'mt-4'} flex justify-end`}>
        <a
          href="/succession/valuation/multi"
          className="inline-flex items-center text-sm font-medium text-succession-primary hover:text-succession-primary-hover transition-colors group p-0"
        >
          {intl.formatMessage({ id: 'succession.launchpad.see_valuation' })}
          <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
        </a>
      </div>
    </div>
  );
};

// Active State Component with Multi-segment Donut
const ActiveState = ({ data, lastUpdated, showHeaderIcon = true, compact = false, t12NoteEnabled = false, intl }) => {
  const linesArr = data?.lines || [];
  const weights = data?.weights || {};
  const features = data?.features || {};
  const methodEnabled = getMethodEnabled(features, weights);

  // Total mid values by method across lines
  const methodTotals = {
    mor: { mid: 0 },
    dcf: { mid: 0 },
    ebitda: { mid: 0 },
    asset: { mid: 0 },
  };
  linesArr.forEach((l) => {
    const m = l?.methods || {};
    if (m.mor && typeof m.mor.mid === 'number') methodTotals.mor.mid += m.mor.mid;
    if (m.dcf && typeof m.dcf.mid === 'number') methodTotals.dcf.mid += m.dcf.mid;
    // Use EBITDA valuation (mid), not EBITDA dollar amount (ebitda)
    if (m.ebitda && typeof m.ebitda.mid === 'number') methodTotals.ebitda.mid += m.ebitda.mid;
    if (m.asset && typeof m.asset.mid === 'number') methodTotals.asset.mid += m.asset.mid;
  });

  const weightedContrib = {
    mor: methodTotals.mor.mid * (weights.mor || 0),
    dcf: methodTotals.dcf.mid * (weights.dcf || 0),
    ebitda: methodTotals.ebitda.mid * (weights.ebitda || 0),
    asset: methodTotals.asset.mid * (weights.asset || 0),
  };

  // Apply normalization impacts (same idea as MultiLineValuation Summary donut)
  const hasAdjustments = (Number(data?.adjustment_impact?.dcf || 0) !== 0) || (Number(data?.adjustment_impact?.ebitda || 0) !== 0);
  if (hasAdjustments) {
    const dcfImpact = Number(data?.adjustment_impact?.dcf || 0);
    const ebitdaImpact = Number(data?.adjustment_impact?.ebitda || 0);
    if (methodTotals.dcf.mid > 0 && dcfImpact !== 0) {
      weightedContrib.dcf = weightedContrib.dcf * (1 + (dcfImpact / methodTotals.dcf.mid));
    }
    if (methodTotals.ebitda.mid > 0 && ebitdaImpact !== 0) {
      weightedContrib.ebitda = weightedContrib.ebitda * (1 + (ebitdaImpact / methodTotals.ebitda.mid));
    }
  }

  const order = ['dcf', 'mor', 'ebitda', 'asset'].filter((m) => methodEnabled[m]);
  const methodColors = {
    dcf: 'var(--succession-primary, #5489B6)',
    mor: 'var(--succession-secondary, #002949)',
    ebitda: 'var(--succession-accent, #5B5E5A)',
    asset: 'var(--succession-neutral, #666666)',
  };
  const methodLabels = {
    dcf: 'DCF',
    mor: 'MOR',
    ebitda: 'EBITDA',
    asset: 'Asset-Based',
  };

  const segments = order.map((k) => ({
    key: k,
    label: methodLabels[k],
    value: Number(weightedContrib[k] || 0),
    color: methodColors[k],
  }));
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 0;

  // SVG donut chart parameters - smaller for compact mode (sidebar)
  const size = compact ? 144 : 205;
  const strokeWidth = compact ? 24 : 32;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Build segments with dash offsets
  let offset = 0;
  const rings = segments.map((seg) => {
    const pct = total > 0 ? (seg.value / total) : 0;
    const dash = pct * circumference;
    const ring = {
      ...seg,
      dash,
      dashOffset: -offset,
    };
    offset += dash;
    return ring;
  });

  const formattedLastUpdated = formatRelativeTime(lastUpdated);
  // Use backend's combined_summary_adjusted.mid (matches "Overall Indication of Value" on main page)
  // Only fall back to client-side total when backend value is truly null/undefined
  const backendMid = data?.combined_summary_adjusted?.mid ?? data?.combined_summary?.mid;
  const centerValue = backendMid != null ? Number(backendMid) : total;

  // Debug: show what value is being used
  if (process.env.NODE_ENV === 'development') {
    console.log('[MultiLineValuationSnapshot ActiveState]', {
      backendMid,
      total,
      centerValue,
      usingBackend: backendMid != null,
    });
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`${compact ? 'mb-4' : 'mb-6'} ${showHeaderIcon ? "flex items-start gap-4" : ""}`}>
        {showHeaderIcon && (
          <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-succession-success/10">
            <TrendingUpIcon className="w-6 h-6 text-succession-success" />
          </div>
        )}
        <div>
          <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{intl.formatMessage({ id: 'succession.launchpad.valuation_snapshot_title' })}</h3>
          <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>
            {intl.formatMessage({ id: 'succession.launchpad.valuation_snapshot_subtitle_methodology' })}
          </p>
        </div>
      </div>

      {/* Donut Chart */}
      <div className={`flex justify-center flex-1 ${compact ? 'mb-4' : 'mb-8'}`}>
        <div className="relative" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="transform -rotate-90">
            {/* Background circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="var(--succession-bg-secondary, #f3f4f6)"
              strokeWidth={strokeWidth}
            />

            {/* Segments */}
            {rings.map((seg) => (
              <circle
                key={seg.key}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={seg.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${seg.dash} ${circumference}`}
                strokeDashoffset={seg.dashOffset}
                strokeLinecap="round"
              />
            ))}
          </svg>

          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className={`${compact ? 'text-2xl' : 'text-4xl'} font-bold text-succession-text-primary`} style={{ margin: 0 }}>
              {formatCurrency(centerValue)}
            </p>
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-succession-text-secondary mt-1`} style={{ margin: 0 }}>
              {intl.formatMessage({ id: 'succession.launchpad.indication_of_value' })}
            </p>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className={`grid ${compact ? 'grid-cols-2 gap-2 py-4' : 'grid-cols-2 gap-4 py-6'} border-t border-succession-border`}>
        {segments.slice(0, 4).map((seg) => (
          <div key={seg.key} className={`flex items-start ${compact ? 'gap-2' : 'gap-3'}`}>
            <div
              className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} rounded-sm mt-1 flex-shrink-0`}
              style={{ backgroundColor: seg.color }}
            />
            <div>
              <div className="flex items-center gap-1">
                <p className={`${compact ? 'text-xs' : 'font-medium'} text-succession-text-primary`} style={{ margin: 0 }}>
                  {seg.label}
                </p>
                {APPROACH_TOOLTIPS[seg.key] && (
                  <InfoTooltip
                    content={seg.key === 'dcf' && t12NoteEnabled
                      ? `${APPROACH_TOOLTIPS[seg.key]} ${T12_YEAR_END_NOTE}`
                      : APPROACH_TOOLTIPS[seg.key]}
                    className={compact ? 'scale-90' : ''}
                  />
                )}
              </div>
              <p className={`${compact ? 'text-sm' : 'text-lg'} font-semibold text-succession-text-primary`} style={{ margin: 0 }}>
                {formatCurrency(seg.value)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Last Updated */}
      {formattedLastUpdated && (
        <div className="flex items-center gap-2 text-sm text-succession-text-secondary">
          <CalendarIcon className="w-4 h-4" />
          <span>{intl.formatMessage({ id: 'succession.launchpad.last_updated' }, { time: formattedLastUpdated })}</span>
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end">
        <a
          href="/succession/valuation/multi"
          className="inline-flex items-center text-sm font-medium text-succession-primary hover:text-succession-primary-hover transition-colors group p-0"
        >
          {intl.formatMessage({ id: 'succession.launchpad.see_valuation' })}
          <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
        </a>
      </div>
    </div>
  );
};

const MultiLineValuationSnapshot = ({
  showHeaderIcon = true,
  compact = false,
  className = '',
}) => {
  const intl = useIntl();
  // Keep same gating behavior as ValuationSnapshot: only show after user has accessed valuation once
  const { data: cu, loading: cuLoading, error: cuError } = useQuery(GET_CURRENT_USER_VAL, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const lastAccessed = cu?.getCurrentUser?.valuation_last_accessed_date;
  // 1× trailing 12-month gross production — the low end of the DCF valuation range.
  const grossProduction = Number(cu?.getCurrentUser?.gross_production || 0);
  const companyFeatures = cu?.getCurrentUser?.company?.features || [];
  const summaryEnabled = Array.isArray(companyFeatures) && companyFeatures.some(
    (f) => String(f) === 'multi_line_valuation_summary' || String(f) === 'multi_line_valuation_summary_type'
  );
  // DCF valuation range (1× T12 gross production floor → DCF ceiling), gated by feature flag.
  const dcfRangeEnabled = Array.isArray(companyFeatures) && companyFeatures.includes('multi_line_valuation_dcf_range');
  // Clarify that T12 is a fiscal year-end snapshot (RJA/ICD feed-driven tenants).
  const t12NoteEnabled = Array.isArray(companyFeatures) && companyFeatures.includes('valuation_t12_year_end_note');
  const canCreate = true;
  const shouldSkipSnapshot = cuLoading || !lastAccessed;

  // Build the same input the valuation page sends after its auto-benchmark
  // recalc, so the snapshot's numbers match what the user sees there. Without
  // user inputs the backend falls back to user.gross_expenses for OpEx, which
  // produces a different (higher) DCF than the page's benchmark-driven value.
  const snapshotInput = useMemo(
    () => (cu?.getCurrentUser ? buildSnapshotInput(cu.getCurrentUser) : null),
    [cu?.getCurrentUser]
  );

  const { data, loading, error } = useQuery(GET_MULTI_LINE_VALUATION_SNAPSHOT, {
    variables: { input: snapshotInput },
    skip: shouldSkipSnapshot || !snapshotInput,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const overallLoading = cuLoading || (!shouldSkipSnapshot && loading);
  const overallError = cuError || error;

  if (overallLoading) {
    return (
      <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 h-full ${className}`}>
        <div className="flex items-start gap-4">
          {showHeaderIcon && <div className="w-12 h-12 rounded-full bg-succession-bg-secondary animate-pulse" />}
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-succession-bg-secondary rounded w-32 animate-pulse" />
            <div className="h-4 bg-succession-bg-secondary rounded w-48 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (overallError) {
    return (
      <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 h-full ${className}`}>
        <div className="text-sm text-succession-error">{intl.formatMessage({ id: 'succession.launchpad.unable_to_load_valuation' })}</div>
      </div>
    );
  }

  const snapshot = data?.multiLineValuation || null;
  const hasData = Boolean(snapshot && snapshot.lines && snapshot.lines.length > 0);

  // Debug: log what the backend is returning for the summary values
  if (snapshot && process.env.NODE_ENV === 'development') {
    console.log('[MultiLineValuationSnapshot] Backend data:', {
      combined_summary_adjusted: snapshot.combined_summary_adjusted,
      combined_summary: snapshot.combined_summary,
      adjustment_impact: snapshot.adjustment_impact,
    });
  }

  return (
    <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border ${compact ? 'p-4' : 'p-6'} hover:shadow-succession-lg transition-shadow h-full ${className}`}>
      {!hasData && <EmptyState canCreate={canCreate} showHeaderIcon={showHeaderIcon} intl={intl} />}
      {hasData && summaryEnabled && (
        <ActiveState
          data={snapshot}
          lastUpdated={lastAccessed}
          showHeaderIcon={showHeaderIcon}
          compact={compact}
          t12NoteEnabled={t12NoteEnabled}
          intl={intl}
        />
      )}
      {hasData && !summaryEnabled && (
        <MethodologyTileList
          data={snapshot}
          lastUpdated={lastAccessed}
          showHeaderIcon={showHeaderIcon}
          compact={compact}
          t12NoteEnabled={t12NoteEnabled}
          grossProduction={grossProduction}
          rangeEnabled={dcfRangeEnabled}
          intl={intl}
        />
      )}
    </div>
  );
};

export default MultiLineValuationSnapshot;


